import React, { useMemo } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RouteKey from './key';
import PrivateRoute from '../modules/profile/components/PrivateRoute';
import { Role } from '../models';
import StartSubjectAmendments from '../pages/subject/StartSubjectAmendment';
import ReviewParticularsPage from '../pages/subject/ReviewParticularsPage';
import ConfirmAmendmentDetailsPage from '../pages/subject/ConfirmAmendmentDetailsPage';
import AmendmentReceiptPage from '../pages/subject/AmendmentReceiptPage';
import ViewDetailsPage from '../pages/subject/ViewDetailsPage';
import Listing from '../pages/examRegistration/Listing';
import EditSubjectPage from '../pages/subject/EditSubjectPage';
import AddSubjectPage from '../pages/subject/AddSubjectPage';
import ReviewSubjectPage from '../pages/subject/ReviewSubjectPage';
import TransactionHistoryAmendmentPage from '../pages/subject/TransactionHistoryAmendmentPage';
import CoordinatorStartAmendment from '../pages/groupSubjectAmendment/00_CoordinatorStartAmendment';
import CoordinatorSelectCandidate from '../pages/groupSubjectAmendment/01_CoordinatorSelectCandidate';
import MakeAmendmentRequestListing from '../pages/groupSubjectAmendment/MakeAmendmentRequestListing';

const { error: errorRoute } = RouteKey.child;
const {
  registration,
  editRegistration,
  reviewParticulars,
  confirmAmendmentDetails,
  receipt,
  viewDetails,
  examRegistrations,
  addSubject,
  reviewSubjects,
  transactionHistoryAmendment,
  coordinatorExamRegistrations,
  coordinatorRegistration,
  coordinatorSelectCandidate,
  coordinatorEditRegistration,
  coordinatorAddSubject,
  coordinatorReviewSubjects,
  coordinatorReviewParticulars,
  coordinatorConfirmAmendmentDetails,
  coordinatorViewCandidateReceipt,
  coordinatorViewDetails,
} = RouteKey.amendment.child;

export const ExamAmendmentNavigationContext = React.createContext<{
  toStartAmendmentInstruction: string;
  toEditSubject: string;
  toAddSubject: string;
  toReviewSubjects: string;
  toReviewParticulars: string;
  toConfirmDetails: string;
}>({
  toStartAmendmentInstruction: '',
  toEditSubject: '',
  toReviewSubjects: '',
  toAddSubject: '',
  toReviewParticulars: '',
  toConfirmDetails: '',
});

const SubjectAmendmentRoute = () => {
  const { path } = useRouteMatch();
  const { t } = useTranslation();

  const routeSteps = [
    {
      label: t('amendment->>SubjectAmendment.Edit Registered Subjects(s)'),
    },
    {
      label: t('amendment->>SubjectAmendment.Add Subject'),
    },
    {
      label: t('amendment->>SubjectAmendment.Review Subject(s)'),
    },
    {
      label: t('amendment->>SubjectAmendment.Review Particulars'),
    },
    {
      label: t('amendment->>SubjectAmendment.Confirm Amendment'),
    },
  ];

  const coordinatorRouteSteps = [
    {
      label: t('amendment->>GroupSubjectAmendment.SelectCandidate'),
    },
    {
      label: t('amendment->>GroupSubjectAmendment.EditRegisteredSubjects'),
    },
    {
      label: t('amendment->>GroupSubjectAmendment.AddSubject'),
    },
    {
      label: t('amendment->>GroupSubjectAmendment.ReviewSubject'),
    },
    {
      label: t('amendment->>GroupSubjectAmendment.ReviewParticulars'),
    },
    {
      label: t('amendment->>GroupSubjectAmendment.SubmitAmendment'),
    },
  ];

  const ExamAmendmentNavigationContextValue = useMemo(
    () => ({
      toStartAmendmentInstruction: `${path}/${examRegistrations.route}/${registration.route}`,
      toEditSubject: `${path}/${examRegistrations.route}/:uuid/${editRegistration.route}`,
      toAddSubject: `${path}/${examRegistrations.route}/:uuid/${addSubject.route}`,
      toReviewSubjects: `${path}/${examRegistrations.route}/:uuid/${reviewSubjects.route}`,
      toReviewParticulars: `${path}/${examRegistrations.route}/:uuid/${reviewParticulars.route}`,
      toConfirmDetails: `${path}/${examRegistrations.route}/:uuid/${confirmAmendmentDetails.route}`,
    }),
    [path]
  );

  return (
    <ExamAmendmentNavigationContext.Provider value={ExamAmendmentNavigationContextValue}>
      <Switch>
        <PrivateRoute exact path={`${path}/${examRegistrations.route}`} roles={[Role.candidate]}>
          <Listing />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/${registration.route}`}
          roles={[Role.candidate]}
        >
          <StartSubjectAmendments />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${editRegistration.route}`}
          roles={[Role.candidate]}
        >
          <EditSubjectPage routeSteps={routeSteps} />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${addSubject.route}`}
          roles={[Role.candidate]}
        >
          <AddSubjectPage routeSteps={routeSteps} />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${reviewSubjects.route}`}
          roles={[Role.candidate]}
        >
          <ReviewSubjectPage routeSteps={routeSteps} />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${reviewParticulars.route}`}
          roles={[Role.candidate]}
        >
          <ReviewParticularsPage routeSteps={routeSteps} />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${confirmAmendmentDetails.route}`}
          roles={[Role.candidate]}
        >
          <ConfirmAmendmentDetailsPage routeSteps={routeSteps} />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${receipt.route}`}
          roles={[Role.candidate]}
        >
          <AmendmentReceiptPage />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${viewDetails.route}`}
          roles={[Role.candidate]}
        >
          <ViewDetailsPage />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${transactionHistoryAmendment.route}`}
          roles={[Role.candidate]}
        >
          <TransactionHistoryAmendmentPage />
        </PrivateRoute>

        {/** Coordinator Subject Amendment */}
        <PrivateRoute
          exact
          path={`${path}/${coordinatorExamRegistrations.route}`}
          roles={[Role.coordinator]}
        >
          <MakeAmendmentRequestListing />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${coordinatorExamRegistrations.route}/${coordinatorRegistration.route}/:examTypeCode`}
          roles={[Role.coordinator]}
        >
          <CoordinatorStartAmendment />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${coordinatorExamRegistrations.route}/${coordinatorSelectCandidate.route}/:examTypeCode`}
          roles={[Role.coordinator]}
        >
          <CoordinatorSelectCandidate routeSteps={coordinatorRouteSteps} />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${coordinatorExamRegistrations.route}/${coordinatorEditRegistration.route}/:uuid`}
          roles={[Role.coordinator]}
        >
          <EditSubjectPage routeSteps={coordinatorRouteSteps} isGroup />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${coordinatorExamRegistrations.route}/${coordinatorAddSubject.route}/:uuid`}
          roles={[Role.coordinator]}
        >
          <AddSubjectPage routeSteps={coordinatorRouteSteps} isGroup />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${coordinatorExamRegistrations.route}/${coordinatorReviewSubjects.route}/:uuid`}
          roles={[Role.coordinator]}
        >
          <ReviewSubjectPage routeSteps={coordinatorRouteSteps} isGroup />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${coordinatorExamRegistrations.route}/${coordinatorReviewParticulars.route}/:uuid`}
          roles={[Role.coordinator]}
        >
          <ReviewParticularsPage routeSteps={coordinatorRouteSteps} isGroup />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${coordinatorExamRegistrations.route}/${coordinatorConfirmAmendmentDetails.route}/:uuid`}
          roles={[Role.coordinator]}
        >
          <ConfirmAmendmentDetailsPage routeSteps={coordinatorRouteSteps} isGroup />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${coordinatorExamRegistrations.route}/${coordinatorViewCandidateReceipt.route}/:uuid`}
          roles={[Role.coordinator]}
        >
          <AmendmentReceiptPage isGroup />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${coordinatorExamRegistrations.route}/${coordinatorViewDetails.route}/:uuid`}
          roles={[Role.coordinator]}
        >
          <ViewDetailsPage isGroup />
        </PrivateRoute>

        <Route path="*" exact>
          <Redirect to={`/${errorRoute}`} />
        </Route>
      </Switch>
    </ExamAmendmentNavigationContext.Provider>
  );
};

export default SubjectAmendmentRoute;
